import {
  Component,
  Inject,
  OnDestroy,
  PLATFORM_ID,
  inject
} from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef
} from '@angular/material/dialog';
import { assetsConst } from 'src/app/core/const/assets.const';
import { ProfileSetupComponent } from '../profile-setup/profile-setup.component';
import { ResetPasswordComponent } from '../reset-password/reset-password.component';
import { SignupComponent } from '../signup/signup.component';
import { SpinnerService } from 'src/app/core/services/spinner.service';
import {
  LoginBody,
  OtpLoginSignUp,
  ProfileDetails
} from 'src/app/models/authmodel';
import { AuthService } from 'src/app/core/services/auth.service';
import { Subject, takeUntil } from 'rxjs';
import { environment } from 'src/environments/environment';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ToasterService } from 'src/app/core/services/toastr.service';
import { CommonModule, isPlatformBrowser } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { OtpComponent } from 'src/app/shared/components/otp/otp.component';

@Component({
  selector: 'megastore-verification',
  templateUrl: './verification.component.html',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    MatIconModule,
    OtpComponent,
    MatDialogModule
  ],
  styleUrls: ['./verification.component.scss']
})
export class VerificationComponent implements OnDestroy {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  intervalHandle: any;
  assetsConst = assetsConst;
  otpTimer = 59;
  sendAgain = false;
  otpCode = '';
  destroy$: Subject<boolean> = new Subject<boolean>();
  fcmToken: string | null = null;
  private platFormId = inject(PLATFORM_ID);

  constructor(
    private _spinnerService: SpinnerService,
    private dialog: MatDialog,
    private _authService: AuthService,
    private _toasterService: ToasterService,
    private _translateService: TranslateService,
    @Inject(MAT_DIALOG_DATA) public data: OtpLoginSignUp,
    private dialogRef: MatDialogRef<VerificationComponent>
  ) {
    this._authService.FCMtoken.subscribe(res => (this.fcmToken = res));
    this.startTimer();
  }

  formatTimer(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes < 10 ? '0' : ''}${minutes}:${remainingSeconds < 10 ? '0' : ''}${remainingSeconds}`;
  }

  fetchOtp(event: string): void {
    this.otpCode = event;
  }

  handleDialog(): void {
    const length = this.data.length || 4;
    if (this.otpCode.length !== length) {
      this._toasterService.errToastr(
        this._translateService.instant('VaidateLength')
      );
      return;
    }

    if (this.data.type === 'payment') {
      this.dialogRef.close(this.otpCode);
      return;
    }

    const payload = this.createPayload();
    console.log(payload);
    delete payload.length;
    this._spinnerService.addToLoader('verify');
    const authObservable =
      this.data.type === 'phone' || this.data.type === 'email' ? this._authService.verifyMailOrPhoneOtp(payload) : this._authService.verifyOtp(payload);

    authObservable.pipe(takeUntil(this.destroy$)).subscribe({
      next: (response: ProfileDetails) => {
        this.handleAuthResponse(response);
      },
      error: () => {
        this._spinnerService.removeFromLoader('verify');
      }
    });
  }

  createPayload(): LoginBody {
    const data = JSON.parse(JSON.stringify(this.data));
    delete data.isVerifyingEmail;
    delete data.isVerifyingPhone;
    delete data.password;

    const payload = {
      ...data,
      ...(this.data.type === 'phone' || this.data.type === 'email' ? { code: this.otpCode } : { otp: this.otpCode }),
      deviceToken: this.fcmToken,
      deviceType: 'WEB'
    };
    return payload;
  }

  handleAuthResponse(response: ProfileDetails): void {
    this._spinnerService.removeFromLoader('verify');
    if (isPlatformBrowser(this.platFormId)) {
      switch (this.data.type) {
      case 'login':
        this.handleLogin(response);
        break;
      case 'forgot':
        this.handleForgot(response);
        break;
      case 'phone':
      case 'email':
        this.handleVerificationSuccess(response);
        break;
      default:
        this.handleCompleteProfile(response);
        break;
      }
    }
  }

  handleLogin(response: ProfileDetails): void {
    if (isPlatformBrowser(this.platFormId)) {
      localStorage.setItem(environment.storageKey, response.accessToken);
      localStorage.removeItem(environment.rememberMeKey);
      this.stopTimer();
      sessionStorage.clear();
      this._authService.profileDetails.next(response);
      this._authService.loggedIn.next(true);
      this.dialogRef.close();
    }
    this._toasterService.successToastr(
      this._translateService.instant('LoginSuccess')
    );
  }

  handleForgot(response: ProfileDetails): void {
    if (isPlatformBrowser(this.platFormId)) {
      localStorage.setItem(environment.storageKey, response.accessToken);
      this._toasterService.successToastr(
        this._translateService.instant('setNewPass')
      );
      this.stopTimer();
      sessionStorage.removeItem(environment.verificationTimer);
      this.dialogRef.close();
    }
    this.dialog.open(ResetPasswordComponent, {
      panelClass: ['modall', 'otp_modal']
    });
  }

  handleVerificationSuccess(response: ProfileDetails): void {
    const toastMessage =
      this.data.type === 'phone' ? 'phoneVerifySuccesss' : 'emailVerfiSuccess';
    this._toasterService.successToastr(
      this._translateService.instant(toastMessage)
    );
    if (isPlatformBrowser(this.platFormId)) {
      this.stopTimer();
      sessionStorage.removeItem(environment.verificationTimer);
      if (
        this.data.type === 'email' &&
        this.data.phoneVerified === false &&
        this.data.phoneNo
      ) {
        response.isPhoneVerified = this.data.phoneVerified;
        response.phoneNo = this.data.phoneNo;
        response.dialCode = this.data.dialCode;
      } else if (this.data.type !== 'email' && this.data.email && this.data.emailVerified === false) {
        response.isEmailVerified = this.data.emailVerified;
        response.email = this.data.email;
      }

      this._authService.profileDetails.next(response);
      this.dialogRef.close({ ...response,
        type: this.data.type });
    }
  }

  handleCompleteProfile(response: ProfileDetails): void {
    if (isPlatformBrowser(this.platFormId)) {
      localStorage.setItem(environment.storageKey, response.accessToken);
      this._toasterService.successToastr(
        this._translateService.instant('completeProfile')
      );
      this._authService.profileDetails.next(response);
      this.stopTimer();
      sessionStorage.removeItem(environment.verificationTimer);
      this.dialogRef.close();
    }
    this.dialog.open(ProfileSetupComponent, {
      panelClass: ['modall', 'otp_modal'],
      data: this.createPayload()
    });
  }

  startTimer(): void {
    if (isPlatformBrowser(this.platFormId)) {
      this.intervalHandle = setInterval(() => {
        if (this.otpTimer > 0) {
          if (this.sendAgain) this.sendAgain = false;
          this.otpTimer--;
          sessionStorage.setItem(
            environment.verificationTimer,
            JSON.stringify(this.otpTimer)
          );
        } else {
          this.sendAgain = true;
          clearInterval(this.intervalHandle);
        }
      }, 1000);
    }
  }

  resendOtp(): void {
    this.sendAgain = false;
    this.otpTimer = 59;
    this.resendApiHit();
    this.startTimer();
  }

  resendApiHit(): void {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let body: any = {};
    let requestObservable;

    switch (this.data.type) {
    case 'login':
    case 'phone':
      body = {
        dialCode: this.data.dialCode,
        phoneNo: this.data.phoneNo
      };
      requestObservable =
          this.data.type === 'login' ? this._authService.loginUserByPhone(body) : this._authService.verifyEmailOrPhone(body);
      break;
    case 'signUp':
      if ('email' in this.data) {
        body = {
          email: this.data.email,
          password: this.data.password,
          confirmPassword: this.data.password
        };
        requestObservable = this._authService.signUpUser(body);
      } else {
        body = {
          dialCode: this.data.dialCode,
          phoneNo: this.data.phoneNo
        };
        requestObservable = this._authService.signUpUser(body);
      }
      break;
    case 'email':
      body = { email: this.data.email };
      requestObservable = this._authService.verifyEmailOrPhone(body);
      break;
    case 'forgot':
      body = { email: this.data.email };
      requestObservable = this._authService.forgotPassword(body);
      break;
    case 'payment':
      this.dialogRef.close('RESEND');
      return;
    default:
      return;
    }

    this._spinnerService.addToLoader('resendOtp');
    requestObservable.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this._spinnerService.removeFromLoader('resendOtp');
        this._toasterService.successToastr(
          this._translateService.instant('OtpResentSuccess')
        );
      },
      error: () => {
        this._spinnerService.removeFromLoader('resendOtp');
      }
    });
  }

  stopTimer(): void {
    if (this.intervalHandle) {
      clearInterval(this.intervalHandle);
    }
  }

  goBackToSignup(): void {
    // Stop the timer and close current modal
    this.stopTimer();
    
    // Prepare data for signup component (only include signup-relevant fields)
    const signupData: any = {
      email: this.data.email,
      password: this.data.password,
      phoneNo: this.data.phoneNo,
      dialCode: this.data.dialCode
    };
    
    // Close verification modal and reopen signup modal
    this.dialogRef.close();
    
    this.dialog.open(SignupComponent, {
      panelClass: ['modall', 'login_nodal'],
      data: signupData
    });
  }

  ngOnDestroy(): void {
    this.destroy$.complete();
    this.destroy$.unsubscribe();
  }
}
